// PDF / Print utilities — no external dependencies, uses a print-optimised popup window.

const BASE_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Georgia, 'Times New Roman', serif; background: #fff; color: #111;
         padding: 48px; font-size: 13px; line-height: 1.65; }
  h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
  h2 { font-size: 14px; font-weight: 700; margin-bottom: 10px; letter-spacing: 0.3px; }
  .logo { font-size: 26px; font-weight: 900; letter-spacing: -1px; color: #111; }
  .logo span { color: #16a34a; }
  .header { display: flex; justify-content: space-between; align-items: flex-start;
            margin-bottom: 28px; padding-bottom: 20px; border-bottom: 2px solid #111; }
  .section { margin-bottom: 22px; }
  .row { display: flex; justify-content: space-between; align-items: center;
         padding: 7px 0; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
  .row:last-child { border-bottom: none; }
  .label { color: #6b7280; }
  .value { font-weight: 600; color: #111; }
  .amount-box { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px;
                padding: 18px 24px; text-align: center; margin: 20px 0; }
  .amount { font-size: 30px; font-weight: 900; color: #16a34a; }
  .badge { display: inline-block; padding: 3px 12px; border-radius: 20px;
           font-size: 11px; font-weight: 700; letter-spacing: 0.5px; }
  .badge-paid { background: #dcfce7; color: #166534; }
  .sigs { margin-top: 44px; }
  .sig-line { margin-bottom: 28px; border-bottom: 1px solid #111;
              padding-bottom: 4px; display: flex; gap: 24px; }
  .sig-label { font-size: 11px; color: #6b7280; min-width: 160px; }
  .footer { margin-top: 40px; padding-top: 14px; border-top: 1px solid #e5e7eb;
            font-size: 11px; color: #9ca3af; }
  table { width: 100%; border-collapse: collapse; margin-top: 2px; }
  th { text-align: left; font-size: 11px; color: #6b7280; text-transform: uppercase;
       letter-spacing: 0.6px; padding: 7px 8px 7px 0; border-bottom: 2px solid #111; }
  td { padding: 8px 8px 8px 0; border-bottom: 1px solid #f3f4f6; font-size: 12px; }
  .good { color: #16a34a; font-weight: 700; }
  .fair { color: #ca8a04; font-weight: 700; }
  .poor { color: #dc2626; font-weight: 700; }
  .sec-head { background: #f9fafb; padding: 7px 10px; font-weight: 700; font-size: 13px;
              border-left: 3px solid #111; margin: 18px 0 0; }
  .stats-row { display: flex; gap: 16px; margin-bottom: 24px; }
  .stat-box { padding: 10px 18px; border-radius: 6px; text-align: center; flex: 1; }
  .stat-num { font-size: 24px; font-weight: 900; }
  .stat-lbl { font-size: 11px; margin-top: 2px; text-transform: uppercase; letter-spacing: 0.5px; }
  @media print {
    body { padding: 32px; }
    .no-print { display: none; }
  }
`;

// ── Core ──────────────────────────────────────────────────────────────────────

export function printDocument(html) {
  const win = window.open('', '_blank', 'width=920,height=720');
  if (!win) {
    // eslint-disable-next-line no-alert
    alert('Please allow pop-ups to generate PDFs.');
    return;
  }
  win.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>RentLord Document</title>
  <style>${BASE_STYLES}</style>
</head>
<body>${html}</body>
</html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt$(n) {
  return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d + (String(d).length === 10 ? 'T12:00:00' : '')).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch { return String(d); }
}

function nowStr() {
  return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function esc(str) {
  return String(str ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#x27;');
}

// ── Receipt ───────────────────────────────────────────────────────────────────

export function generateReceiptHTML(payment, tenantName, unit) {
  const ref    = esc(payment.reference_no || payment.ref || '—');
  const amount = fmt$(payment.amount  || payment.amt);
  const date   = fmtDate(payment.payment_date || payment.date);
  const period = esc(payment.mth_name || (payment.payment_date || payment.date || '').slice(0, 7));
  const method = esc(payment.method   || payment.mth || '—');

  return `
    <div class="header">
      <div>
        <div class="logo">Rent<span>Lord</span></div>
        <div style="font-size:11px;color:#6b7280;margin-top:3px;text-transform:uppercase;letter-spacing:1px">
          Official Rent Receipt
        </div>
      </div>
      <span class="badge badge-paid">PAID</span>
    </div>

    <div class="section">
      <h2>Receipt Details</h2>
      <div class="row"><span class="label">Receipt #</span>
        <span class="value" style="font-family:monospace">${ref}</span></div>
      <div class="row"><span class="label">Tenant</span>
        <span class="value">${esc(tenantName)}</span></div>
      <div class="row"><span class="label">Unit</span>
        <span class="value">${esc(unit)}</span></div>
      <div class="row"><span class="label">Rental Period</span>
        <span class="value">${period}</span></div>
      <div class="row"><span class="label">Payment Date</span>
        <span class="value">${date}</span></div>
      <div class="row"><span class="label">Payment Method</span>
        <span class="value">${method}</span></div>
    </div>

    <div class="amount-box">
      <div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px">
        Amount Paid
      </div>
      <div class="amount">${amount}</div>
    </div>

    <div class="footer">
      This receipt was generated by RentLord on ${nowStr()} and serves as official proof of payment. ·
      rentlordapp.com
    </div>
  `;
}

// ── Notice to Vacate ──────────────────────────────────────────────────────────

export function generateNoticeHTML(tenant, lease, form, vacateDate) {
  const tenantName = esc(tenant?.full_name || tenant?.name || '—');
  const property   = esc(tenant?.property  || lease?.property || lease?.p || '—');
  const unit       = esc(tenant?.unit      || tenant?.u || lease?.unit    || '—');
  const reason     = esc(form.reason);
  const notes      = esc(form.notes);
  const noticeType = esc(form.notice_type);
  const vDate      = esc(vacateDate);

  return `
    <div class="header">
      <div>
        <div class="logo">Rent<span>Lord</span></div>
        <div style="font-size:11px;color:#6b7280;margin-top:3px;text-transform:uppercase;letter-spacing:1px">
          Official Legal Notice
        </div>
      </div>
      <div style="text-align:right">
        <div style="font-size:20px;font-weight:900">${noticeType}</div>
        <div style="font-size:12px;color:#6b7280;margin-top:3px">Date: ${fmtDate(form.delivery_date)}</div>
      </div>
    </div>

    <div class="section">
      <h2>Tenant Information</h2>
      <div class="row"><span class="label">Tenant Name</span><span class="value">${tenantName}</span></div>
      <div class="row"><span class="label">Property</span><span class="value">${property}</span></div>
      <div class="row"><span class="label">Unit</span><span class="value">${unit}</span></div>
      <div class="row"><span class="label">Notice Delivered</span><span class="value">${fmtDate(form.delivery_date)}</span></div>
      <div class="row"><span class="label">Required Vacate Date</span>
        <span class="value" style="color:#dc2626;font-size:15px">${vDate}</span></div>
    </div>

    <div class="section">
      <h2>Notice</h2>
      <p style="margin-bottom:14px">
        This notice is to inform you that you are required to vacate and surrender possession of the
        above-referenced premises by <strong style="color:#dc2626">${vDate}</strong>.
      </p>
      <p style="margin-bottom:10px"><strong>Reason:</strong> ${reason}.</p>
      ${notes ? `<p style="margin-bottom:10px"><strong>Additional Notes:</strong> ${notes}</p>` : ''}
      <p>
        If you fail to vacate the premises by the date stated above, legal proceedings to remove you
        from the premises may be initiated against you. This may affect your rental history and credit.
      </p>
    </div>

    <div class="sigs">
      <div class="sig-line">
        <span class="sig-label">Landlord Signature</span>
        <span style="flex:1"></span>
        <span class="sig-label">Date</span>
        <span style="flex:0.5"></span>
      </div>
      <div class="sig-line">
        <span class="sig-label">Tenant Signature (acknowledges receipt)</span>
        <span style="flex:1"></span>
        <span class="sig-label">Date</span>
        <span style="flex:0.5"></span>
      </div>
    </div>

    <div class="footer">
      This notice was delivered on ${fmtDate(form.delivery_date)} and constitutes official notice under
      applicable state law. Generated by RentLord · rentlordapp.com · ${nowStr()}
    </div>
  `;
}

// ── Inspection Checklist ──────────────────────────────────────────────────────

const COND_CLASS = { Good: 'good', Fair: 'fair', Poor: 'poor', 'N/A': '' };

export function generateChecklistHTML(checks, tenantName, checkType, sections) {
  const typeLabel = checkType === 'movein' ? 'Move-In' : checkType === 'moveout' ? 'Move-Out' : 'Routine';
  const total = Object.keys(checks).length;
  const good  = Object.values(checks).filter(v => v.condition === 'Good').length;
  const poor  = Object.values(checks).filter(v => v.condition === 'Poor').length;

  const sectionHTML = (sections || []).map(sec => {
    const rows = sec.items.map(item => {
      const key  = `${sec.section}::${item}`;
      const val  = checks[key] || { condition: 'Good', note: '' };
      const cls  = COND_CLASS[val.condition] || '';
      return `<tr>
        <td>${esc(item)}</td>
        <td class="${cls}">${esc(val.condition)}</td>
        <td style="color:#6b7280">${esc(val.note || '')}</td>
      </tr>`;
    }).join('');

    return `
      <div class="sec-head">${esc(sec.section)}</div>
      <table>
        <thead><tr><th style="width:40%">Item</th><th style="width:20%">Condition</th><th>Notes</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }).join('');

  return `
    <div class="header">
      <div>
        <div class="logo">Rent<span>Lord</span></div>
        <div style="font-size:11px;color:#6b7280;margin-top:3px;text-transform:uppercase;letter-spacing:1px">
          ${typeLabel} Inspection Checklist
        </div>
      </div>
      <div style="text-align:right;font-size:12px;color:#6b7280">
        <div>Tenant: <strong style="color:#111">${esc(tenantName)}</strong></div>
        <div style="margin-top:4px">Date: <strong style="color:#111">${nowStr()}</strong></div>
      </div>
    </div>

    <div class="stats-row">
      <div class="stat-box" style="background:#f0fdf4">
        <div class="stat-num" style="color:#16a34a">${good}</div>
        <div class="stat-lbl" style="color:#166534">Good</div>
      </div>
      <div class="stat-box" style="background:#fef2f2">
        <div class="stat-num" style="color:#dc2626">${poor}</div>
        <div class="stat-lbl" style="color:#991b1b">Issues</div>
      </div>
      <div class="stat-box" style="background:#f9fafb">
        <div class="stat-num" style="color:#111">${total}</div>
        <div class="stat-lbl" style="color:#6b7280">Total</div>
      </div>
    </div>

    ${sectionHTML}

    <div class="sigs" style="margin-top:36px">
      <div class="sig-line">
        <span class="sig-label">Landlord Signature</span>
        <span style="flex:1"></span>
        <span class="sig-label">Date</span>
        <span style="flex:0.5"></span>
      </div>
      <div class="sig-line">
        <span class="sig-label">Tenant Signature</span>
        <span style="flex:1"></span>
        <span class="sig-label">Date</span>
        <span style="flex:0.5"></span>
      </div>
    </div>

    <div class="footer">
      Generated by RentLord on ${nowStr()} · rentlordapp.com · This document serves as an official
      ${typeLabel} inspection record and may be used in deposit dispute proceedings.
    </div>
  `;
}
